import { isScene, worldOrder, type StoryCtx, type StoryNode } from '@dispositio/core';
import type { CanvasModel } from './canvasModel';

/**
 * Von Szene zu Szene auf der Handlungsfläche (PF 11): die Szenen, die die Fläche zeigt (Marken an Zeitlinien und lose Karten), in der
 * Reihenfolge der Weltzeit; ihre Nachbarn und der Sprung mit der Tastatur.
 */

/** Szenen auf der Fläche in der Reihenfolge der Weltzeit */
export function canvasSceneOrder(ctx: StoryCtx, model: CanvasModel): string[] {
  const ids = new Set(model.nodes.filter((v) => (v.role === 'event' || v.role === 'period' || v.role === 'card') && v.id === v.nodeId).map((v) => v.nodeId));
  const scenes = [...ids].map((id) => ctx.nodes.get(id)).filter((n): n is StoryNode => !!n && isScene(n));
  return worldOrder(ctx, scenes).map((n) => n.id);
}

/** Die Szene davor und danach in der Weltzeit (soweit es sie gibt) */
export function neighborsIn(order: readonly string[], id: string): string[] {
  const i = order.indexOf(id);
  return i < 0 ? [] : [order[i - 1], order[i + 1]].filter((x): x is string => !!x);
}

export type SceneStep = 'prev' | 'next' | 'first' | 'last';

/** Wohin ein Sprung führt: davor bzw. danach (ohne Auswahl: an den Anfang bzw. das Ende), die erste bzw. letzte; null: nirgends hin */
export function stepScene(order: readonly string[], current: string | null, step: SceneStep): string | null {
  if (order.length === 0) return null;
  const i = current ? order.indexOf(current) : -1;
  if (step === 'first') return order[0]!;
  if (step === 'last') return order.at(-1)!;
  if (i < 0) return step === 'next' ? order[0]! : order.at(-1)!;
  return order[step === 'next' ? i + 1 : i - 1] ?? null;
}

/** Taste → Sprung (←/→, Pos1/Ende) */
export const STEP_KEYS: Record<string, SceneStep> = { ArrowLeft: 'prev', ArrowRight: 'next', Home: 'first', End: 'last' };
